import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";

import { PageHeader } from "../shell/PageHeader";
import {
  useGetV1Series,
  useGetV1Alerts,
  useGetV1KpiSeries,
  useGetV1SitesIdNow,
  type EnvelopeSiteNowDataFlowNodesItem,
  type Figure,
  type InterventionSummaryItem,
} from "@solarkey/api-client";
import {
  Banner,
  EmptyState,
  ErrorState,
  Metric,
  Money,
  Skeleton,
  StateWord,
} from "@solarkey/ui";
import { useHasLensOptional } from "../session/capabilities";
import { TimeSeriesTile } from "@solarkey/charts";
import { TrendCaret, TrendPanel } from "../components/TrendTileRow";
import { SiteTabBar } from "../components/SiteTabBar";
import { SiteNowCommercial, type CommercialSiteNow } from "./SiteNowCommercial";

export function SiteNow(
  { site, period, from, to }: { site: string; period?: string; from?: string; to?: string },
): React.ReactNode {
  const [prBasis, setPrBasis] = useState<string>("ABSOLUTE");
  const [availBasis, setAvailBasis] = useState<string>("PLANT");
  const [trendKey, setTrendKey] = useState<string | null>(null);
  // Which half of "Needs attention" is showing. Declared up here with the other
  // hooks and NOT beside the data it drives: this screen returns early for its
  // loading and error states, so a `useState` further down runs on some renders
  // and not others — React counts hooks by call order, and the mismatch took the
  // whole screen out with "Rendered more hooks than during the previous render".
  // `null` means "not chosen yet", so the default can follow the data below.
  const [attnTab, setAttnTab] = useState<"alarms" | "findings" | null>(null);
  const navigate = useNavigate();
  // Money is lens-gated (UX-001): a reader without the value lens sees no amounts.
  // The OPTIONAL variant because Site: Now also renders in the edge tree, which
  // mounts no CapabilitiesProvider — the throwing hook took that screen down.
  const hasValueLens = useHasLensOptional("value");
  // "now" once per mount — a fresh timestamp each render changes the trend
  // query key and loops the fetch forever (Sudesh; SensorView.tsx pattern).
  const now = useMemo(() => new Date().toISOString(), []);
  // `from`/`to` only matter for period=custom; the URL builder forwards any
  // params, so they reach the backend without a contract regen.
  // What the EQUIPMENT is reporting right now, beside what the platform's rules
  // say. Its own query because the Site: Now contract carries no device alarms —
  // and a plant can be sitting on a live Major Fault while `attention` reports
  // only that a device stopped talking.
  const alarmQuery = useGetV1Alerts({ site, min_severity: 1 });

  const query = useGetV1SitesIdNow(
    site,
    { period, ...(period === "custom" && from && to ? { from, to } : {}) } as Parameters<typeof useGetV1SitesIdNow>[1],
  );

  if (query.isLoading) {
    return <SiteNowSkeleton />;
  }

  if (query.isError || !query.data || query.data.status !== 200) {
    return (
      <ErrorState
        title="Site Now is unavailable"
        message="The live view could not be loaded. The last good values are not shown to avoid implying they are current."
        onRetry={() => void query.refetch()}
      />
    );
  }

  const envelope = query.data.data;
  const d = envelope.data;

  // A commercial (C&I) site's asset graph carries storage + genset nodes, so the view
  // model renders the screen-21 composition instead of the utility one (same P2 page).
  // The backend serves that composition for no site today, so the dispatch happens
  // ONLY when the payload actually carries it — otherwise the user would land on a
  // screen that reads undefined and white-screens. Without it, the standard
  // composition below renders, every figure of which is really served.
  const isCommercial = (d as Record<string, unknown>).site_class === "commercial";
  if (isCommercial && hasCommercialPayload(d)) {
    return <SiteNowCommercial site={site} data={d as unknown as CommercialSiteNow} />;
  }

  const tiles = d.tiles;
  const quiet = envelope.meta.quiet;
  // The tiles aggregate the SELECTED period, so every period-bearing label is
  // driven by meta.period — never a hardcoded "today".
  const periodWord = periodLabel(envelope.meta.period);
  // One local day — today, or a single past day — versus a span of them. Taken from
  // the window the SERVER resolved, so the chart and the figures above it can never
  // describe different spans.
  // Which day the curve is OF. "over range" is the right label for a span of days
  // but says nothing when the span is one: a reader who picked 25 Oct wants the
  // chart to confirm it is the 25th they are looking at.
  const dayWord = (() => {
    const start = envelope.meta.period?.start;
    if (!start || envelope.meta.period?.label === "today") return periodWord;
    const [y, m, d] = start.slice(0, 10).split("-").map(Number);
    if (!y || !m || !d) return periodWord;
    return new Date(y, m - 1, d).toLocaleDateString(undefined, {
      day: "numeric", month: "short", year: "numeric",
    });
  })();
  const oneDay = (() => {
    const pd = envelope.meta.period;
    if (!pd?.start) return true;
    return !pd.end || pd.end === pd.start;
  })();
  const blocks = d.blocks ?? [];
  const attention = d.attention ?? [];
  // Still-raised only: the list is "needs attention", not "happened this week".
  // `active` is the register's own verdict — the flag is up in the latest reading —
  // so a fault that has since cleared does not keep demanding a response.
  const openAlarms = ((alarmQuery.data?.data?.data?.alerts ?? []) as SiteAlarm[])
    .filter((a) => a.active)
    .sort((a, b) => Number(!!b.is_critical) - Number(!!a.is_critical));
  const criticalAlarms = openAlarms.filter((a) => a.is_critical);
  const defaultTab = openAlarms.length > 0 || attention.length === 0 ? "alarms" : "findings";
  const attnTab_ = attnTab ?? defaultTab;
  const hasSeriesRef = !!(d.series_ref?.site && d.series_ref?.from && d.series_ref?.to);
  const flowNodes = d.flow?.nodes ?? [];
  const siteHeader = (d as Record<string, unknown>).site_header as
    | { name: string; slug: string; dc_capacity_kwp: number | null; ac_capacity_kw: number | null; block_labels: string[] }
    | undefined;

  return (
    <div className="flex flex-col gap-[14px]">
      {/* ── Page header: site name + capacity subtitle, mode + freshness + Raise work order ── */}
      <PageHeader
        title={siteHeader?.name ?? site}
        scope={formatCapacityScope(siteHeader)}
        // Periods, not modes. The header already carries a freshness chip reading
        // "Live · 5 m", so a Live/Replay/Forecast toggle beside it said the same
        // thing twice — and occupied the place a reader looks for Today / 7D / 30D,
        // which this screen offered nowhere at all.
        timeControl="periods"
        timeOptions={["today", "7d", "30d", "1y", "custom"]}
        // Step a day at a time, the way the reference platform does: reaching
        // yesterday through a date picker is three clicks, the arrow is one, and
        // walking back through a week is the commonest thing an engineer does here.
        // The shared header already owns a router-connected stepper — using it
        // keeps one definition and gets the PUSH navigation with it, so Back
        // returns to the previous day rather than leaving the screen. Several days
        // are still chosen through Custom; stepping is for one day at a time.
        showDateNav
        action={
          /* UX-REV-001 U2 / UX-007: an unavailable action is HIDDEN, not rendered as
             a disabled control that explains a backend gap. "Raise work order"
             returns when the CMMS write path exists. */
          <SiteFreshnessChip dataPath={tiles?.data_path} />
        }
      />
      <SiteTabBar site={site} />

      {isCommercial ? (
        <Banner tone="info">
          This site is registered as commercial (C&amp;I). The C&amp;I composition
          (storage, genset and load mix) is not shown: the platform holds no storage,
          genset or load-meter data for it. The figures below are the standard ones.
        </Banner>
      ) : null}

      {/* ── Row 1: 6 headline tiles — forced 6-column grid ── */}
      <section aria-labelledby="site-now-tiles">
        <h2 id="site-now-tiles" className="sr-only">Headline figures</h2>
        <div className="sk-tiles-6">
          {/* 1 — Active power (trend disclosure, UX-007 §9) */}
          <div className={`sk-tile${trendKey === "power" ? " open" : ""}`}>
            {tiles?.active_power ? (
              <TrendCaret open={trendKey === "power"} onClick={() => setTrendKey(trendKey === "power" ? null : "power")} />
            ) : null}
            <span className="sk-tile__label">Active power</span>
            {tiles?.active_power ? (
              <>
                <Metric figure={tiles.active_power} showBasis={false} showProvenance={false} />
                {(() => {
                  const p = figureNumber(tiles.active_power);
                  const ac = siteHeader?.ac_capacity_kw;
                  const pct = p !== undefined && ac && ac > 0 && p > 0 ? `${Math.round((p / ac) * 100)}% of AC` : null;
                  const prov = tiles.active_power?.provenance === "measured" ? "measured" : tiles.active_power?.provenance;
                  if (!pct && !prov) return null;
                  return (
                    <span className="sk-tile__sub">
                      {pct ? <span>{pct}</span> : null}
                      {pct && prov ? <span>·</span> : null}
                      {prov ? <span className="sk-basis-chip">{prov}</span> : null}
                    </span>
                  );
                })()}
              </>
            ) : <StateWord state="NOT_MEASURED" />}
          </div>

          {/* 2 — Energy today (annotation ①) */}
          <div className={`sk-tile${trendKey === "energy" ? " open" : ""}`}>
            {tiles?.energy_today?.delivered ? (
              <TrendCaret open={trendKey === "energy"} onClick={() => setTrendKey(trendKey === "energy" ? null : "energy")} />
            ) : null}
            <span className="sk-tile__label">Energy {periodWord}</span>
            {tiles?.energy_today?.delivered ? (
              <>
                <Metric figure={toDisplayEnergy(tiles.energy_today.delivered)!} showBasis={false} showFreshness={false} showProvenance={false} maximumFractionDigits={1} />
                <span className="sk-tile__sub">
                  {tiles.energy_today.delivered.basis ? (
                    <span className="sk-basis-chip">{tiles.energy_today.delivered.basis}</span>
                  ) : null}
                  {tiles.energy_today.reconciliation_band ? (
                    <>
                      {" reconciled "}
                      <Metric className="sk-recon" figure={tiles.energy_today.reconciliation_band} showBasis={false} showProvenance={false} showFreshness={false} />
                    </>
                  ) : null}
                </span>
              </>
            ) : <StateWord state="NOT_MEASURED" />}
          </div>

          {/* 3 — PR with clickable variant chips (annotation ②) */}
          <VariantTile
            label="PR"
            tile={tiles?.pr}
            basis={prBasis}
            onBasis={setPrBasis}
            labelOf={prBasisLabel}
          />

          {/* 4 — Availability with clickable variant chips.
                 The sub-line names WHICH availability is shown: the server serves
                 technical (time-based, capacity-weighted) and grid availability.
                 There is no energy-by-cause availability here (ruling §6.1 forbids
                 an unqualified or mislabelled availability). */}
          <VariantTile
            label="Availability"
            tile={tiles?.availability}
            basis={availBasis}
            onBasis={setAvailBasis}
            labelOf={availabilityVariantLabel}
            sub={availabilityBasisSub(availBasis)}
          />

          {/* 5 — Coverage (no intraday series → no trend disclosure).
                 The served figure is devices reporting / monitored devices — device
                 level, not signal level. The per-signal gaps are the partially
                 monitored count, and only stated when the server verified them. */}
          <div className="sk-tile">
            <span className="sk-tile__label">Coverage</span>
            {tiles?.coverage?.signal_level ? (
              typeof tiles.coverage.signal_level.value === "number" ? (
                <>
                  <Metric figure={tiles.coverage.signal_level} showBasis={false} showFreshness={false} showProvenance={false} />
                  <span className="sk-tile__sub">
                    device level
                    {coverageSignalNote(tiles.coverage)}
                  </span>
                </>
              ) : (
                // UX-REV-002 M4: a withheld coverage is a quiet state, not a hero
                // value — contain the word rather than the 16px tile size.
                <StateWord
                  state={tiles.coverage.signal_level.state ?? "CANNOT_CHECK"}
                  reason={tiles.coverage.signal_level.reason}
                  style={{ fontSize: "13px", fontWeight: 400, color: "var(--sk-color-ink-3)" }}
                />
              )
            ) : (
              <StateWord state="NOT_MEASURED" style={{ fontSize: "13px", fontWeight: 400, color: "var(--sk-color-ink-3)" }} />
            )}
          </div>

          {/* 6 — Data path (annotation ③) */}
          <DataPathTile dataPath={tiles?.data_path} />
        </div>
        {/* Trend panel opens directly under the tile row (UX-007 §9). */}
        {trendKey === "power" && tiles?.active_power ? (
          <div style={{ marginTop: 10 }}>
            <TrendPanel
              title="Active power"
              series={{
                site,
                signal: "ac_active_power",
                from: new Date(new Date().setHours(0, 0, 0, 0)).toISOString(),
                to: now,
                unit: tiles.active_power.unit ?? "kW",
                label: "today",
              }}
              onClose={() => setTrendKey(null)}
            />
          </div>
        ) : null}
        {trendKey === "energy" && tiles?.energy_today?.delivered ? (
          <div style={{ marginTop: 10 }}>
            <TrendPanel
              title="Energy today"
              series={{
                site,
                // The daily-reset energy register (kWh) → a real 0→today curve;
                // "energy_delivered" is a computed KPI, not a /v1/series metric.
                signal: "energy_active_daily",
                from: new Date(new Date().setHours(0, 0, 0, 0)).toISOString(),
                to: now,
                unit: tiles.energy_today.delivered.unit ?? "kWh",
                label: "today",
              }}
              onClose={() => setTrendKey(null)}
            />
          </div>
        ) : null}
        {/* PR, Availability and Coverage are computed daily KPIs with no intraday
            time-series in the contract, so they carry no trend disclosure (a broken
            empty chart is worse than none). Add one here if/when the engine serves an
            intraday series for them. */}
      </section>

      {/* ── Second stat row (UX-007 §8): 6 reference figures ── */}
      <div className="sk-kvstrip">
        {[
          { label: `Insolation ${periodWord}`, figure: tiles?.insolation, sub: "plane of array" },
          { label: `Specific yield ${periodWord}`, figure: tiles?.specific_yield, sub: "on DC nameplate" },
          { label: `CUF ${periodWord}`, figure: tiles?.cuf, sub: "AC nameplate" },
          { label: `Peak power ${periodWord}`, figure: scalePower(tiles?.peak_power), sub: peakAtSub(tiles?.peak_power) },
          { label: `CO₂ avoided ${periodWord}`, figure: tiles?.co2_avoided, sub: "region pack" },
          { label: "Lifetime energy", figure: scaleEnergyBig(tiles?.lifetime_energy), sub: lifetimeSinceSub(tiles?.lifetime_energy) },
        ].map((c) => (
          <div key={c.label} className="sk-kvstrip__item">
            <span className="sk-kvstrip__label">{c.label}</span>
            {c.figure && c.figure.value != null && !isWithheldFig(c.figure) ? (
              <Metric figure={c.figure} showBasis={false} showFreshness={false} showProvenance={false} maximumFractionDigits={2} />
            ) : (
              <StateWord state={(c.figure?.state as "CANNOT_CHECK" | "NOT_MEASURED" | undefined) ?? "NOT_MEASURED"} />
            )}
            {c.sub ? <span className="sk-kvstrip__sub">{c.sub}</span> : null}
          </div>
        ))}
        {/* Revenue is a MONEY figure, not a plain one, so it cannot ride the map
            above: it needs `Money` (currency + locale) and the value lens. The
            contract has served `tiles.revenue` all along (site_now.py:581) and no
            screen rendered it — the figure was computed, stored and invisible
            (QA, 25 Oct). Withheld reads as a state word here, never as zero. */}
        {hasValueLens && tiles?.revenue ? (
          <div className="sk-kvstrip__item">
            <span className="sk-kvstrip__label">{`Revenue ${periodWord}`}</span>
            {tiles.revenue.amount != null ? (
              <Money money={tiles.revenue} />
            ) : (
              <StateWord
                state={
                  (tiles.revenue.state as "CANNOT_CHECK" | "NOT_MEASURED" | undefined) ??
                  "CANNOT_CHECK"
                }
              />
            )}
            <span className="sk-kvstrip__sub">on the effective tariff</span>
          </div>
        ) : null}
      </div>

      {/* ── Row 2: Power flow (1.4fr) + Needs attention (1fr) ── */}
      <div className="sk-site-cols">
        {/* Power flow (annotation ④) */}
        <section aria-labelledby="site-now-flow" className="sk-section">
          <div className="sk-section__head">
            <div>
              <h2 id="site-now-flow" className="sk-section__title">Power flow</h2>
              <span className="sk-section__sub">now · owner view; schematic for engineers</span>
            </div>
            <div className="flex gap-1" role="group" aria-label="Flow view">
              <button
                type="button"
                aria-pressed={true}
                className="rounded px-2 py-1 font-medium border border-accent bg-accent-soft text-accent"
                style={{ fontSize: 10 }}
              >
                Bus
              </button>
              {/* Schematic is the full P8 single-line diagram — its own page. */}
              <button
                type="button"
                className="rounded px-2 py-1 font-medium border border-border text-text-muted hover:bg-surface-hover"
                style={{ fontSize: 10 }}
                onClick={() => void navigate({ to: "../schematic" as never })}
              >
                Schematic
              </button>
            </div>
          </div>

          {flowNodes.length > 0 ? (
            <div className="flex flex-col gap-density-gap">
              <div className="sk-flowbus">
                {/* Generation column (left): solar, storage, backup … */}
                <FlowColumn
                  nodes={flowNodes.filter((n) => FLOW_GEN_KINDS.includes(n.kind ?? ""))}
                  side="gen"
                />

                <div className="sk-flowbar" aria-hidden="true">Plant bus</div>

                {/* Export / load column (right): grid, auxiliary, load … */}
                <FlowColumn
                  nodes={flowNodes.filter((n) => FLOW_LOAD_KINDS.includes(n.kind ?? ""))}
                  side="load"
                />
              </div>
            </div>
          ) : (
            <EmptyState variant="no_data" title="No power flow data" />
          )}
        </section>

        {/* Needs attention (annotation ⑤) */}
        <section aria-labelledby="site-now-attention" className="sk-section">
          <div className="sk-section__head">
            <div>
              <h2 id="site-now-attention" className="sk-section__title">Needs attention</h2>
              <span className="sk-section__sub">
                {criticalAlarms.length > 0
                  ? `${criticalAlarms.length} critical open`
                  : openAlarms.length > 0
                    ? `${openAlarms.length} alarm${openAlarms.length !== 1 ? "s" : ""} ringing`
                    : "nothing ringing"}
              </span>
            </div>
            {/* Alarms and findings answer different questions — what the equipment
                reported, and what the platform concluded — so they get a toggle
                rather than one merged list a reader has to sort in their head.
                Counts on the tabs, so switching is never a guess at what is behind
                the other one. */}
            <div className="sk-attn-tabs" role="tablist" aria-label="Attention view">
              <button type="button" role="tab" aria-selected={attnTab_ === "alarms"}
                      className={attnTab_ === "alarms" ? "sk-attn-tab on" : "sk-attn-tab"}
                      onClick={() => setAttnTab("alarms")}>
                Alarms {openAlarms.length > 0 ? `· ${openAlarms.length}` : ""}
              </button>
              <button type="button" role="tab" aria-selected={attnTab_ === "findings"}
                      className={attnTab_ === "findings" ? "sk-attn-tab on" : "sk-attn-tab"}
                      onClick={() => setAttnTab("findings")}>
                Findings {attention.length > 0 ? `· ${attention.length}` : ""}
              </button>
            </div>
          </div>

          {attention.length + openAlarms.length > 0 ? (
            <>
              <div className="flex flex-col gap-2">
                {/* A CRITICAL alarm is shown whichever tab is open. A toggle is a
                    convenience; a plant on fire is not something a reader should
                    have to have chosen the right tab to find out about. */}
                {attnTab_ === "findings"
                  ? criticalAlarms.map((a, i) => (
                      <DeviceAlarmCard key={`crit:${a.device}:${a.code}:${i}`} alarm={a} />
                    ))
                  : null}
                {attnTab_ === "alarms"
                  ? openAlarms.map((a, i) => (
                      <DeviceAlarmCard key={`${a.device}:${a.code}:${i}`} alarm={a} />
                    ))
                  : attention.map((item, i) => (
                      <AttentionCard key={item.id ?? i} item={item} />
                    ))}
                {/* The tab a reader chose can legitimately be empty while the other
                    is not — say which, rather than showing a blank panel. */}
                {attnTab_ === "alarms" && openAlarms.length === 0 ? (
                  <p className="text-xs text-text-muted">
                    No alarm ringing. {attention.length} platform finding
                    {attention.length === 1 ? "" : "s"} on the other tab.
                  </p>
                ) : null}
                {attnTab_ === "findings" && attention.length === 0 ? (
                  <p className="text-xs text-text-muted">
                    No platform finding. {openAlarms.length} alarm
                    {openAlarms.length === 1 ? "" : "s"} on the other tab.
                  </p>
                ) : null}
              </div>
              {/* Verified quiet statement (annotation ⑥) */}
              {quiet?.text ? (
                <div className="sk-quiet-footer">
                  <p className="text-xs text-text-muted">{quiet.text}</p>
                </div>
              ) : null}
              {/* Blocks one-liner (wireframe 16): deviation from median */}
              {blocks.length > 0 ? (
                <div className="sk-blocks-oneliner">
                  Blocks against expected:{" "}
                  {[...blocks]
                    .sort((a, b) => devRank(b.deviation) - devRank(a.deviation))
                    .map((b, i) => {
                      const dev = figureNumber(b.deviation);
                      const sign = dev !== undefined && dev >= 0 ? "+" : "";
                      return (
                        <span key={b.block_id ?? i} style={{ whiteSpace: "nowrap" }}>
                          {i > 0 ? " · " : ""}
                          <b>{b.label ?? b.block_id}</b>{" "}
                          {dev !== undefined ? (
                            `${sign}${dev.toFixed(1)}%`
                          ) : (
                            <StateWord
                              state={(b.deviation?.state as "CANNOT_CHECK" | "NOT_MEASURED" | undefined) ?? "NOT_MEASURED"}
                              reason={b.deviation?.reason}
                            />
                          )}
                        </span>
                      );
                    })}{" "}
                  <button
                    type="button"
                    style={openBlocksSmallStyle}
                    onClick={() => void navigate({ to: "../blocks" as never })}
                  >
                    Open blocks
                  </button>
                </div>
              ) : null}
            </>
          ) : (
            /* No items: center the verified-quiet state in the stretched card. */
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {/* Verified quiet is the SERVER's claim (meta.quiet). Without it the
                  screen says only what it checked — the rule-based detections —
                  and never asserts that the data path or anything else is healthy. */}
              {quiet?.text ? (
                <EmptyState
                  variant="verified_quiet"
                  title="Nothing needs attention"
                  message={quiet.text}
                />
              ) : (
                /* UX-REV-001 U1: one quiet line, not a paragraph explaining which
                   checks ran. The caveat (not every cause is checked yet) rides as
                   a tooltip — kept, but voiced for a layman, not on the glass. */
                <EmptyState
                  variant="withheld"
                  title="No open findings"
                  message="Nothing was found needing attention today. Not every possible cause is checked yet, so this is not a full all-clear."
                />
              )}
            </div>
          )}
        </section>
      </div>

      {/* ── Row 3: Expected vs delivered (1.2fr) + Waterfall (1fr) ── */}
      <div className="sk-site-cols-wide">
        {/* Measured power (Sudesh, 14 Oct). What the plant actually produced, and
            nothing else: the stored metric the server itself nominates in
            `series_ref.signal`. No model, no band, no second curve — the
            expected-vs-delivered envelope that used to sit here is gone, and a
            chart of a model we no longer run would be worse than this one.

            The basis is named in the subtitle because the ruling requires it:
            at site scope `ac_active_power` is the INVERTER AC sum (the read is
            restricted to the generating devices, so it is not a double count),
            and §3.4 says an inverter sum must never be labelled a meter
            reading. "Measured" is the honest word for it; "metered" would not
            be. */}
        <section aria-labelledby="site-now-power" className="sk-section">
          <div className="sk-section__head">
            <div>
              <h2 id="site-now-power" className="sk-section__title" style={{ whiteSpace: "nowrap" }}>
                {oneDay ? "Generation" : "Energy by day"}
              </h2>
              <span className="sk-section__sub">
                {oneDay
                  ? `${dayWord} · power vs irradiance`
                  : `${periodWord} · one bar per day, with that day's sunlight`}
              </span>
            </div>
          </div>
          {/* A window is a different QUESTION depending on its length. One day asks
              what the plant did through the day — a curve. Several ask which days
              were weak and whether the weather explains them, which an 8,640-point
              intraday curve cannot show at two pixels a sample. */}
          {!oneDay ? (
            <DailyEnergyAndSun
              site={site}
              from={envelope.meta.period?.start ?? ""}
              to={envelope.meta.period?.end ?? ""}
            />
          ) : hasSeriesRef ? (
            <MeasuredPower
              site={d.series_ref!.site!}
              signal={d.series_ref!.signal ?? "ac_active_power"}
              from={d.series_ref!.from!}
              to={d.series_ref!.to!}
            />
          ) : (
            <div style={{ height: 220, display: "grid", placeItems: "center",
                          color: "var(--sk-color-ink-3)", fontSize: 11 }}>
              No series window for this site
            </div>
          )}
        </section>

        {/* Where the energy went — loss waterfall (annotation ⑧) */}
        <WaterfallCard />
      </div>
    </div>
  );
}

/* ── Attention card (wireframe: title + time, energy at stake, verdict, proposed action, Open) ── */
/** An alarm the EQUIPMENT raised, beside the rules the platform ran.
 *
 *  "Needs attention" listed only platform detections, so a plant could be sitting
 *  on a live Major Fault from its own inverter while the card said the one thing
 *  worth saying was that a device had stopped reporting. The fault is the more
 *  specific statement — it names the device and what it reported — and it belongs
 *  first. The footer distinguishes the two: a rule the platform ran is not the
 *  same claim as a word the inverter itself set, and a reader acts on them
 *  differently.
 */
export function DeviceAlarmCard({ alarm }: { alarm: SiteAlarm }): React.ReactNode {
  const title = [alarm.label || alarm.code, alarm.device].filter(Boolean).join(" · ");
  return (
    <div className="sk-ticket" data-sev={alarm.is_critical ? "fault" : "warn"}>
      <b>{title || "Device alarm"}</b>
      {alarm.fault_level_label ? (
        <span style={{ color: "var(--sk-color-ink-2)" }}>{alarm.fault_level_label}</span>
      ) : null}
      {alarm.fault_level_meaning ? (
        <span style={{ fontSize: 11, color: "var(--sk-color-ink-3)" }}>
          {alarm.fault_level_meaning}
        </span>
      ) : null}
      <div className="sk-ticket__x">
        <span style={{ fontSize: 11, color: "var(--sk-color-ink-3)" }}>
          device-reported
          {typeof alarm.count === "number" && alarm.count > 1 ? ` · ${alarm.count}x` : ""}
          {alarm.last_seen
            ? ` · last ${new Date(alarm.last_seen).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
            : ""}
        </span>
      </div>
    </div>
  );
}

/** Only the fields this card reads. The alerts contract is wide and most of it is
 *  for the Alerts screen's own table. */
export type SiteAlarm = {
  device?: string | null;
  code?: string | null;
  label?: string | null;
  fault_level_label?: string | null;
  fault_level_meaning?: string | null;
  is_critical?: boolean | null;
  active?: boolean | null;
  count?: number | null;
  last_seen?: string | null;
};

function AttentionCard({ item }: { item: InterventionSummaryItem }): React.ReactNode {
  // The headline is the detection's own title + reason; the equipment id when it
  // names one. Never the old "stopped since <opened_at>" — opened_at is just when
  // the detection ran, not a stoppage time, so it mislabelled e.g. a low-PR
  // finding as "stopped".
  const title = [item.title || kindLabel(item.class), item.equipment_id]
    .filter(Boolean).join(" · ") || "Intervention";
  const flaggedText = item.opened_at
    ? `flagged ${new Date(item.opened_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
    : undefined;

  return (
    // The card's severity is the detection's own, not "fault" for everything.
    // `severity` is served but not yet declared in the contract, hence the cast.
    <div
      className="sk-ticket"
      data-sev={severityTone((item as { severity?: string }).severity)}
    >
      <b>{title}</b>
      {item.what ? (
        <span style={{ color: "var(--sk-color-ink-2)" }}>{item.what}</span>
      ) : null}
      {flaggedText ? (
        <span style={{ fontSize: 11, color: "var(--sk-color-ink-3)" }}>{flaggedText}</span>
      ) : null}

      {item.value_at_stake?.energy ? (
        <span className="sk-ticket__v">
          <Metric figure={item.value_at_stake.energy} showBasis={false} showProvenance={false} showFreshness={false} />
          <span style={{ color: "var(--sk-color-ink-3)" }}> at stake today</span>
        </span>
      ) : null}

      {/* No verdict engine exists in Build-01: the lane is the literal "detected"
          and the executor the literal "none", so neither a verdict nor a proposed
          action is printed. A rule-based detection says what it detected, nothing
          more (ruling §6.1). The lane is shown only once it is a real verdict. */}
      {item.lane && item.lane !== "detected" ? (
        <div style={{ color: "var(--sk-color-ink-2)", marginTop: 2 }}>
          Verdict: {kindLabel(item.lane)}
        </div>
      ) : null}

      <div className="sk-ticket__x">
        <span style={{ fontSize: 11, color: "var(--sk-color-ink-3)" }}>
          {item.executor && item.executor !== "none"
            ? `proposed: ${kindLabel(item.executor)}`
            : "rule-based detection"}
        </span>
        {/* UX-REV-001 U2 / UX-007: the unbuilt intervention-detail control is hidden,
            not rendered disabled with an explanation. It returns when the view exists. */}
      </div>
    </div>
  );
}

/** A detection's severity as the card's tone — never "fault" for everything. */
function severityTone(severity?: string | null): "fault" | "warning" | "info" {
  if (severity === "high" || severity === "fault" || severity === "critical") return "fault";
  if (severity === "warning") return "warning";
  return "info";
}

/* ── Loss waterfall card (wireframe 16 annotation ⑧) ── */
//
// Site: Now does NOT carry a loss breakdown. The contract serves tiles, blocks,
// attention, flow, series_ref and site_header — there is no `waterfall` — so this
// card has one honest state and names the screen that does have it.
//
// It used to read `d.waterfall` and `waterfall.exported_pct` through a hand-written
// cast, for fields that exist NOWHERE in `backend/src` or in the frozen
// `openapi.yaml`. The `if (!waterfall)` guard therefore always fired, and the ~110
// lines behind it were unreachable while carrying two real defects: a latent
// white-screen (`exported_pct.toFixed(1)` on a field only the cast asserted was
// non-null) and a track axis hardcoded to 92–100%, which would have clamped every
// row to the left edge for any site below 92% of potential — reading as though
// nothing had been lost. Removed rather than kept as scaffolding: the row shape it
// assumed does not match the cascade the backend actually serves, so it was written
// against an imagined contract (QA, 25 Oct).
function WaterfallCard(): React.ReactNode {
  return (
    <section aria-labelledby="site-now-wf" className="sk-section">
      <div className="sk-section__head">
        <div>
          <h2 id="site-now-wf" className="sk-section__title">Where the energy went</h2>
          <span className="sk-section__sub">live day</span>
        </div>
      </div>
      {/* UX-REV-001 U1: one quiet line pointing the reader to where the breakdown
          lives, not a paragraph confessing this endpoint does not carry it. */}
      <EmptyState
        variant="withheld"
        title="No energy breakdown here"
        message="The energy breakdown is shown on the Performance page."
      />
    </section>
  );
}


/* ── KPI tile with clickable variant chips (PR, Availability) ── */
function VariantTile(
  { label, tile, basis, onBasis, labelOf, sub, trendId, currentTrendKey, onTrendKey }: {
    label: string;
    tile?: { selected?: Figure | null; variants?: readonly string[] | null } | null;
    basis: string;
    onBasis: (b: string) => void;
    labelOf: (v: string) => string;
    sub?: string;
    trendId?: string;
    currentTrendKey?: string | null;
    onTrendKey?: (key: string | null) => void;
  },
): React.ReactNode {
  const variantValues = (tile as { variant_values?: Record<string, Figure> } | undefined)?.variant_values;
  const shown = (variantValues ? variantValues[basis] : undefined) ?? tile?.selected ?? undefined;
  const variants = tile?.variants ?? [];
  const isOpen = trendId != null && currentTrendKey === trendId;
  return (
    <div className={`sk-tile${isOpen ? " open" : ""}`}>
      {trendId && shown && onTrendKey ? (
        <TrendCaret open={isOpen} onClick={() => onTrendKey(isOpen ? null : trendId)} />
      ) : null}
      <span className="sk-tile__label">{label}</span>
      {shown ? (
        <>
          <Metric figure={shown} showBasis={false} showFreshness={false} showProvenance={false} />
          {variants.length > 0 ? (
            // UX-REV-002 M3 (= UX-REV-001 U4): a summary tile carries ONE figure and a
            // compact way to switch basis — not the whole method menu laid out as
            // chips. The five-way side-by-side comparison keeps its home on the
            // Performance page. This is the same basis switcher Home already uses; the
            // selection is client-side (every variant is on the wire).
            <label className="basis-switch">
              <span className="muted">basis</span>
              <select
                aria-label={`${label} basis`}
                value={basis}
                onChange={(e) => onBasis(e.target.value)}
                style={{ fieldSizing: "content" } as React.CSSProperties}
              >
                {variants.map((v) => (
                  <option key={v} value={v}>{labelOf(v)}</option>
                ))}
              </select>
            </label>
          ) : null}
          {sub ? <span className="sk-tile__sub">{sub}</span> : null}
        </>
      ) : <StateWord state="NOT_MEASURED" />}
    </div>
  );
}

/* ── Data path tile (annotation ③) ──
   The headline word is the SERVER's site-level statement (it judges the telemetry
   path), never a browser verdict over hops that measure different things: the
   engines hop being stale does not mean the data path is down. Hops that carry no
   signal arrive in `hops_withheld` and are named as unchecked, not drawn live. */
type DataPathTileData = {
  hops?: unknown[];
  last_packet?: string | null;
  site_statement?: string | null;
  hops_withheld?: unknown[];
};

const PATH_WORD: Record<string, string> = {
  DATA_PATH_DOWN: "Path down",
  DATA_PATH_STALE: "Stale",
  DATA_PATH_CANNOT_CHECK: "Cannot check",
  DATA_PATH_NO_SOURCE: "No source",
};

function pathStatement(dataPath?: DataPathTileData): { word: string; live: boolean; statement: string | null } {
  const statement = dataPath?.site_statement;
  if (typeof statement === "string" && statement.length > 0) {
    return { word: PATH_WORD[statement] ?? statement.replace(/_/g, " "), live: false, statement };
  }
  return { word: "Live", live: true, statement: null };
}

function DataPathTile({ dataPath }: { dataPath?: DataPathTileData }): React.ReactNode {
  const hops = ((dataPath?.hops ?? []) as { source_kind?: string; freshness?: { state?: string } }[]);
  const withheld = ((dataPath?.hops_withheld ?? []) as { source_kind?: string }[]);
  const ageText = formatPacketAge(dataPath?.last_packet);
  const { word, live, statement } = pathStatement(dataPath);
  // UX-REV-002 M4: "Cannot check" is an unknown state, not a value or an alert
  // (unlike Path down / Stale) -- contain it rather than render it at hero size.
  const contained = statement === "DATA_PATH_CANNOT_CHECK";

  return (
    <div className="sk-tile">
      <span className="sk-tile__label">Data path</span>
      <span className="sk-live" data-live={live ? "true" : "false"}>
        <span className="sk-live__dot" />
        <span style={contained
          ? { fontFamily: "var(--sk-font-mono)", fontSize: "13px", fontWeight: 400, lineHeight: 1.3, color: "var(--sk-color-ink-3)" }
          : { fontFamily: "var(--sk-font-mono)", fontSize: "21px", fontWeight: 300, lineHeight: 1.15 }}>{word}</span>
      </span>
      {hops.length > 0 ? (
        <span className="sk-tile__sub">
          {hops
            .map((h) => {
              const name = kindLabel(h.source_kind);
              const st = h.freshness?.state;
              return st && st !== "live" ? `${name} (${st.replace(/_/g, " ")})` : name;
            })
            .filter(Boolean)
            .join(" → ")}
          {withheld.length > 0 ? ` · ${withheld.length} hop${withheld.length === 1 ? "" : "s"} not checked` : ""}
          {ageText ? ` · last packet ${ageText}` : ""}
        </span>
      ) : ageText ? (
        <span className="sk-tile__sub">last packet {ageText}</span>
      ) : null}
    </div>
  );
}

/** Site freshness chip — "Live · 12 s" / "Stale" / "Path down", from the server's
 *  own site-level statement. */
function SiteFreshnessChip({ dataPath }: { dataPath?: DataPathTileData }): React.ReactNode {
  const hops = ((dataPath?.hops ?? []) as unknown[]);
  const ageText = formatPacketAge(dataPath?.last_packet);
  if (hops.length === 0 && !ageText) return null;
  const { word, live } = pathStatement(dataPath);
  return (
    <span className="sk-sitefresh" data-live={live ? "true" : "false"}>
      <span className="sk-sitefresh__dot" aria-hidden />
      <span>{word}{ageText ? ` · ${ageText}` : ""}</span>
    </span>
  );
}

/* ── Second-row figure helpers ── */
function isWithheldFig(f?: Figure): boolean {
  const s = f?.state;
  return f == null || f.value == null || s === "CANNOT_CHECK" || s === "NOT_MEASURED" || s === "NOT_APPLICABLE";
}

/** kW → MW when large, so peak power reads like the wireframe. */
function scalePower(f?: Figure): Figure | undefined {
  if (!f || typeof f.value !== "number") return f;
  if (f.unit === "kW" && Math.abs(f.value) >= 1000) return { ...f, value: f.value / 1000, unit: "MW" };
  return f;
}

/** kWh → MWh → GWh for cumulative energy. */
function scaleEnergyBig(f?: Figure): Figure | undefined {
  if (!f || typeof f.value !== "number") return f;
  if (f.unit !== "kWh") return f;
  const v = Math.abs(f.value);
  if (v >= 1e6) return { ...f, value: f.value / 1e6, unit: "GWh" };
  if (v >= 1e3) return { ...f, value: f.value / 1e3, unit: "MWh" };
  return f;
}

/** "at 12:00" from a peak-power figure's `at` timestamp — with the date too when
 *  the peak was not today, since the tile can span a multi-day period. */
function peakAtSub(f?: Figure): string | undefined {
  const at = (f as { at?: string } | undefined)?.at;
  if (!at) return undefined;
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return undefined;
  const hhmm = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  const isToday = d.toDateString() === new Date().toDateString();
  return isToday
    ? `at ${hhmm}`
    : `at ${hhmm} on ${d.toLocaleDateString(undefined, { day: "numeric", month: "short" })}`;
}

/** "since 28 Apr 2026" from a lifetime figure's `since` date. */
function lifetimeSinceSub(f?: Figure): string | undefined {
  const since = (f as { since?: string } | undefined)?.since;
  if (!since) return undefined;
  const d = new Date(since);
  if (Number.isNaN(d.getTime())) return undefined;
  return `since ${d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`;
}

/** "12 s" / "3 m" / "2 h" ago from an ISO timestamp. */
/* ── Measured power (UX-006 §2) ────────────────────────────────────────────
   One line, from one stored metric. The window comes from the server's
   `series_ref`, which is the SAME window the day's figures are summed over, so
   the header and the curve cannot disagree.

   Clamped to generation hours 06:00-19:00 of the data's own day, like every
   other intraday chart here (UX-006 §2) — an unclamped axis spends half its
   width on darkness. A non-numeric point stays `null` rather than becoming 0:
   a gap in the line is a gap in the data, and a zero would read as the plant
   producing nothing. */
/** Several days: one BAR per day for energy, with that day's sunlight as a line.
 *
 *  An intraday curve cannot answer a multi-day question — thirty days of five-minute
 *  power is 8,640 points drawn two pixels apart, and the thing a reader wants from a
 *  range ("which days were weak, and was it the weather?") is invisible in it.
 *
 *  Bars rather than a line because a day's energy is a TOTAL: one value per day, and
 *  a line between two totals invents a trajectory through a day nobody measured. The
 *  sunlight stays a line on its own axis, because the comparison is the point — a
 *  short bar under normal insolation is the plant's fault; a short bar under a dull
 *  day is the weather's.
 *
 *  Both come from `daily_summary` via /v1/kpi/series — the stored engine figure for
 *  a COMPLETED day, the same number the tiles above show, never re-derived here. */
function DailyEnergyAndSun(
  { site, from, to }: { site: string; from: string; to: string },
): React.ReactNode {
  const energy = useGetV1KpiSeries({ metric: "energy", scope: site, from, to });
  const sun = useGetV1KpiSeries({ metric: "insolation", scope: site, from, to });

  if (energy.isLoading) return <Skeleton variant="tile" />;

  type Pt = { day?: string; figure?: { value?: number | null; unit?: string | null } };
  const ePts = (energy.data?.status === 200 ? energy.data.data.data?.points ?? [] : []) as Pt[];
  const sPts = (sun.data?.status === 200 ? sun.data.data.data?.points ?? [] : []) as Pt[];

  if (ePts.length === 0) {
    return (
      <div style={{ height: 260, display: "grid", placeItems: "center",
                    color: "var(--sk-color-ink-3)", fontSize: 11 }}>
        No completed day in this range
      </div>
    );
  }

  // Midday of each local day, so a bar sits under its own date label rather than on
  // the midnight boundary between two.
  const times = ePts.map((pt) => {
    const [y, m, d] = (pt.day ?? "").split("-").map(Number);
    return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1, 12).getTime() / 1000;
  });
  const values = ePts.map((pt) => (typeof pt.figure?.value === "number" ? pt.figure.value : null));

  // Matched by DAY, never by position: a day the engine never computed is absent
  // from one series and present in the other, and zipping would shift the sun onto
  // the wrong bars.
  const sunByDay = new Map<string, number>();
  for (const pt of sPts) {
    if (pt.day && typeof pt.figure?.value === "number") sunByDay.set(pt.day, pt.figure.value);
  }
  const sunValues = ePts.map((pt) => (pt.day ? sunByDay.get(pt.day) ?? null : null));
  const hasSun = sunValues.some((v) => v !== null);

  return (
    <TimeSeriesTile
      label="Energy"
      unit={ePts[0]?.figure?.unit ?? "kWh"}
      kind="bars"
      times={times}
      values={values}
      secondary={hasSun
        ? { label: "Sunlight", unit: "kWh/m²", values: sunValues }
        : undefined}
      height={260}
    />
  );
}

function MeasuredPower(
  { site, signal, from, to }:
  { site: string; signal: string; from: string; to: string },
): React.ReactNode {
  const q = useGetV1Series({ site, signal, from, to });
  // Irradiance on the same window, for the right-hand axis. The pairing is the
  // first thing an O&M reader checks: power dipping with irradiance flat is a
  // plant fault; the same dip with irradiance falling is a cloud. Its own query,
  // because the series endpoint serves one signal at a time — and its absence is
  // not fatal, the power curve simply renders alone.
  const irr = useGetV1Series({ site, signal: "irradiance", from, to });

  if (q.isLoading) return <Skeleton variant="tile" />;

  const ok = q.data?.status === 200 ? q.data.data.data : undefined;
  const points = ok?.points ?? [];
  const times = points.map((pt) => Date.parse(pt.ts) / 1000);
  const values = points.map((pt) => (typeof pt.value === "number" ? pt.value : null));

  // Aligned to the POWER series' timestamps, never zipped by position: the two
  // reads are independent and a missing interval in one would otherwise shift
  // every later point of it against the other, drawing a curve that never happened.
  const irrOk = irr.data?.status === 200 ? irr.data.data.data : undefined;
  const irrAt = new Map<number, number>();
  for (const pt of irrOk?.points ?? []) {
    if (typeof pt.value === "number") irrAt.set(Date.parse(pt.ts) / 1000, pt.value);
  }
  const irrValues = times.map((t) => irrAt.get(t) ?? null);
  const hasIrr = irrValues.some((v) => v !== null);

  if (times.length === 0) {
    return (
      <div style={{ height: 220, display: "grid", placeItems: "center",
                    color: "var(--sk-color-ink-3)", fontSize: 11 }}>
        No measured power for this window
      </div>
    );
  }

  // The axis spans what the plant actually DID, not a fixed 06:00-19:00.
  //
  // The clamp existed to stop a completed day spending half its width on darkness,
  // but it also truncated: a plant still exporting at 19:30 had its evening cut
  // off, and on the live day the axis ran to 19:00 whatever the hour, so a chart
  // read at 11:00 showed eight hours of empty space to the right. Taking the window
  // from the data gives the live day an axis that ends at the newest reading and
  // grows with it — and a past day its own real first and last light.
  const generating = times
    .map((t, i) => ({ t, on: (values[i] ?? 0) > 0 || (irrValues[i] ?? 0) > 0 }))
    .filter((x) => x.on)
    .map((x) => x.t);
  const pad = 15 * 60; // a quarter hour either side, so the curve is not flush
  const xFrom = generating.length > 0 ? generating[0]! - pad : times[0]!;
  const xTo = generating.length > 0 ? generating[generating.length - 1]! + pad : times[times.length - 1]!;

  // Where the readings stop. The window the server resolved runs to NOW on the live
  // day, so a feed that went quiet at 11:20 leaves a flat run to the right edge —
  // which reads as "the plant produced nothing all afternoon", a measured claim,
  // when the truth is that nobody was listening. Marked only when the gap is real:
  // a couple of missing intervals at the end of a 5-minute feed is the next packet
  // being due, not an outage.
  const lastReading = (() => {
    for (let i = times.length - 1; i >= 0; i -= 1) {
      if (values[i] != null) return times[i]!;
    }
    return null;
  })();
  const QUIET_S = 20 * 60;
  const staleAfter = lastReading != null && xTo - lastReading > QUIET_S ? lastReading : null;

  return (
    <TimeSeriesTile
      label="Active power"
      unit={ok?.unit ?? ""}
      times={times}
      values={values}
      secondary={hasIrr
        ? { label: "Irradiance", unit: irrOk?.unit === "W/m2" ? "W/m²" : (irrOk?.unit ?? ""), values: irrValues }
        : undefined}
      xRange={[xFrom, xTo]}
      staleAfter={staleAfter}
      height={260}
    />
  );
}


function formatPacketAge(iso?: string | null): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  const s = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (s < 90) return `${s} s`;
  const m = Math.round(s / 60);
  if (m < 90) return `${m} m`;
  return `${Math.round(m / 60)} h`;
}




/* ── Loading skeleton ── */
function SiteNowSkeleton(): React.ReactNode {
  return (
    <div className="flex flex-col gap-[14px]" aria-busy="true">
      <Skeleton variant="text" width="12rem" />
      <div className="sk-tiles-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} variant="tile" />
        ))}
      </div>
      <div className="sk-site-cols">
        <Skeleton variant="row" lines={4} />
        <Skeleton variant="row" lines={4} />
      </div>
    </div>
  );
}

/* ── Helpers ── */



function figureNumber(f?: Figure): number | undefined {
  if (!f || f.value === null || f.value === undefined) return undefined;
  return typeof f.value === "number" ? f.value : undefined;
}

/** Display energy in MWh when the kWh value is large (wireframe 04 shows MWh). */
function toDisplayEnergy(f?: Figure): Figure | undefined {
  if (!f) return f;
  const v = figureNumber(f);
  if (f.unit === "kWh" && v !== undefined && Math.abs(v) >= 1000) {
    return { ...f, value: v / 1000, unit: "MWh" };
  }
  return f;
}


/* Power-flow bus columns (wireframe 04): generation on the left, export/load on
   the right, busbar between. A node that carries a value is sized by its SHARE of
   the power in its column (the figure is on the node); a withheld node keeps a
   thin fixed slot. There are no invented "not fitted" kinds: a kind the backend
   does not serve is a kind the platform knows nothing about, and the registry was
   never asked — so it is not drawn at all. */
const FLOW_GEN_KINDS = ["solar", "wind", "diesel", "storage", "backup"];
const FLOW_LOAD_KINDS = ["grid", "auxiliary", "evacuation", "load"];
/** A withheld node's slot, relative to the column's measured total. */
const FLOW_WITHHELD_SHARE = 0.18;

/** Rank for ordering blocks by |deviation|, worst first.
 *
 *  A withheld deviation must not rank as ZERO: zero is "exactly on expected", the
 *  best possible reading, so `?? 0` put every unmeasured block in the middle of the
 *  order as though it were on target. `-1` sorts it last instead — unmeasured is not
 *  a performance claim (the ruling: a withheld figure is never zero). Same pattern
 *  the fleet grid already uses to sort withheld sites last. */
function devRank(figure?: Figure | null): number {
  const v = figureNumber(figure ?? undefined);
  return v === undefined ? -1 : Math.abs(v);
}

function FlowColumn(
  { nodes, side }: { nodes: EnvelopeSiteNowDataFlowNodesItem[]; side: "gen" | "load" },
): React.ReactNode {
  const total = nodes.reduce(
    (sum, n) => sum + Math.abs(figureNumber(n.power ?? undefined) ?? 0),
    0,
  );
  return (
    <div className="sk-flowcol">
      {nodes.map((node, i) => {
        const v = figureNumber(node.power ?? undefined);
        // Share of the column's power, with a floor so a tiny-but-real node stays
        // legible; a withheld node gets the fixed slot, never a share it has not
        // earned.
        const share =
          v !== undefined && total > 0
            ? Math.max(Math.abs(v) / total, 0.08)
            : FLOW_WITHHELD_SHARE;
        return (
          <FlowSeg
            key={`${side}-${node.kind ?? i}`}
            kind={node.kind ?? side}
            figure={node.power}
            flex={share}
          />
        );
      })}
    </div>
  );
}

/** One segment in a power-flow column: solid and proportional when it carries a
 *  value, a dashed slot with its state word (and the reason on hover) when not. */
export function FlowSeg(
  { kind, figure, flex }: { kind: string; figure?: Figure | null; flex: number },
): React.ReactNode {
  const v = figureNumber(figure ?? undefined);
  const empty = v === undefined;
  return (
    <div className="sk-flowseg" data-kind={kind} data-empty={empty ? "true" : "false"} style={{ flex }}>
      <span className="sk-flowseg__kind">{kindLabel(kind)}</span>
      {/* The arrows MOVE while power is actually crossing this node, and stand
          still when it is not. Animation reads as "this is happening now", so a
          dark plant whose chevrons kept marching would be the screen telling a
          reader it is generating. At 0 kW they stop. */}
      {!empty ? (
        <span className="sk-flowseg__arrows"
              data-flowing={Math.abs(v ?? 0) > 0 ? "true" : "false"}
              aria-hidden="true">
          {/* Enough chevrons to overfill the widest block here, plus the pitch the
              track slides through: shift a track that only just fits and a gap
              opens at one edge for part of every cycle. */}
          {Array.from({ length: 26 }, (_, i) => <i key={i}>&rsaquo;</i>)}
        </span>
      ) : null}
      <span className="sk-flowseg__val">
        {figure ? (
          <Metric figure={figure} showBasis={false} showProvenance showFreshness={false} />
        ) : (
          <StateWord state="NOT_MEASURED" />
        )}
      </span>
    </div>
  );
}

/** The period in words, from meta.period — never a hardcoded "today". */
/** The window, in the fewest words a TILE LABEL can carry.
 *
 *  All seven uses sit in the uppercase label of a narrow tile in one row. The long
 *  forms overflowed into each other the moment a range was picked — "INSOLATION
 *  2026-09-25 TO 2026-10-25SPECIFIC YIELD 2026-09-25 TO 2026-10-2…" — and even
 *  "last 30 days" crowds a tile that also has to fit a number and a unit.
 *
 *  A custom range names no dates at all: the header states them twice already, in
 *  the Custom chip and in the two date inputs directly above these tiles, and a
 *  third copy on every tile bought nothing but the collision. The label's job here
 *  is to say WHICH window, not to restate it. */
function periodLabel(period?: { label?: string | null; start?: string; end?: string } | null): string {
  switch (period?.label) {
    case "today":
      return "today";
    case "7d":
      return "7D";
    case "30d":
      return "30D";
    case "1y":
      return "1Y";
    case "mtd":
      return "MTD";
    case "qtd":
      return "QTD";
    case "fy":
      return "FY";
    case "lifetime":
      return "lifetime";
    case "custom":
      return "over range";
    default:
      return "over range";
  }
}

/** Which availability is on the tile. The server serves technical (time-based,
 *  capacity-weighted) and grid availability; neither is an energy basis. */
function availabilityBasisSub(basis: string): string {
  if (basis === "GRID") return "grid, time basis";
  if (basis === "PLANT") return "technical · time, capacity-weighted";
  return "time basis";
}

/** The partially-monitored note, stated only when the server verified it. */
function coverageSignalNote(coverage: {
  partially_monitored?: { device_id?: string }[] | null;
}): React.ReactNode {
  const judgement = (coverage as { partial_monitoring?: { state?: string } }).partial_monitoring;
  const list = coverage.partially_monitored ?? [];
  if (judgement?.state !== "MEASURED") {
    return (
      <>
        {" · signal gaps "}
        <StateWord state="CANNOT_CHECK" reason={{ template: "SIGNAL_INVENTORY_UNAVAILABLE" }} />
      </>
    );
  }
  if (list.length === 0) return null;
  return ` · ${list.length} partially monitored`;
}


/** Does the payload actually carry the C&I composition? The backend serves none
 *  of these objects today, and reading them when absent throws. */
function hasCommercialPayload(data: unknown): boolean {
  const d = data as Partial<CommercialSiteNow> | null | undefined;
  return !!(
    d &&
    d.bus?.scenario &&
    Array.isArray(d.bus.scenarios) &&
    Array.isArray(d.balance_tiles) &&
    Array.isArray(d.figures_strip) &&
    d.storage_card?.genset &&
    d.load_sources?.hours &&
    d.savings?.avoided_cost &&
    d.contract_demand_locked &&
    d.site_header
  );
}

const PR_BASIS_LABEL: Record<string, string> = {
  ABSOLUTE: "Absolute", GRID: "Grid", WEATHER: "Weather", EQUIPMENT: "Equipment",
  GRID_WEATHER: "Grid+Weather", GRID_WEATHER_EQUIPMENT: "Grid+Weather+Equipment",
};
function prBasisLabel(v: string): string {
  return PR_BASIS_LABEL[v] ?? v;
}

const AVAIL_VARIANT_LABEL: Record<string, string> = {
  PLANT: "Plant", GRID: "Grid",
};
function availabilityVariantLabel(v: string): string {
  return AVAIL_VARIANT_LABEL[v] ?? v;
}

function formatCapacityScope(
  header?: { dc_capacity_kwp: number | null; ac_capacity_kw: number | null; block_labels: string[] },
): string {
  if (!header) return "The plant’s live state.";
  const parts: string[] = [];
  if (header.dc_capacity_kwp != null) parts.push(`${(header.dc_capacity_kwp / 1000).toFixed(1)} MWp DC`);
  if (header.ac_capacity_kw != null) parts.push(`${(header.ac_capacity_kw / 1000).toFixed(0)} MW AC`);
  if (header.block_labels && header.block_labels.length > 0) {
    const labels = header.block_labels.map((l) => l.replace(/^Block\s*/i, ""));
    parts.push(labels.length > 0 ? `Block ${labels.join(", ")}` : "");
  }
  return parts.length > 0 ? parts.join(" · ") : "The plant’s live state.";
}

const FLOW_KIND_LABEL: Record<string, string> = { evacuation: "Evacuation loss" };
function kindLabel(token?: string): string | undefined {
  if (!token) return undefined;
  return FLOW_KIND_LABEL[token] ?? token.charAt(0).toUpperCase() + token.slice(1).replace(/_/g, " ");
}

/* ── Inline styles for elements that need precise wireframe match ── */

// Buttons mirror wireframe .btn / .btn.primary / .btn.small. The two controls with
// nothing behind them are rendered disabled, so the screen does not offer an action
// the platform cannot perform.
const openBlocksSmallStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  height: 20,
  padding: "0 6px",
  fontSize: "10px",
  fontWeight: 500,
  border: "1px solid var(--sk-color-border-2)",
  borderRadius: "var(--sk-radius-control)",
  background: "var(--sk-color-surface-0)",
  color: "var(--sk-color-ink-1)",
  cursor: "pointer",
  whiteSpace: "nowrap",
};
